import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle, ShieldCheck } from "lucide-react";

// Exclusão de registro com histórico só é possível quando o superadministrador
// ligou o "Modo de teste" desta empresa no Dashboard do SaaS.
export function ForceDeleteDialog({ open, onOpenChange, entityLabel = "registro", name, hasHistory, busy, onConfirm }) {
  const [forceEnabled, setForceEnabled] = useState(null); // null = verificando

  useEffect(() => {
    if (!open || !hasHistory) return undefined;
    let active = true;
    setForceEnabled(null);
    api.get("/admin/force-delete-status")
      .then((r) => { if (active) setForceEnabled(r?.data?.enabled === true); })
      .catch(() => { if (active) setForceEnabled(false); });
    return () => { active = false; };
  }, [open, hasHistory]);

  const blocked = hasHistory && forceEnabled !== true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="force-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-rose-400" /> Exclusão definitiva
          </DialogTitle>
        </DialogHeader>
        {!hasHistory ? (
          <p className="text-sm text-slate-300" data-testid="force-delete-simple-msg">
            Excluir definitivamente {entityLabel} <b className="text-slate-100">{name}</b>? Esta operação não poderá ser desfeita.
          </p>
        ) : forceEnabled === null ? (
          <p className="text-sm text-slate-400" data-testid="force-delete-checking">Verificando permissões...</p>
        ) : forceEnabled ? (
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 p-4 space-y-2" data-testid="force-delete-history-warning">
            <div className="flex items-center gap-2 text-rose-300 font-semibold"><AlertTriangle className="h-5 w-5" /> Este {entityLabel} possui histórico relacionado</div>
            <p className="text-sm text-rose-100">
              O modo de teste está ligado para esta empresa, então os dados dependentes também serão excluídos
              (movimentações, inventários, alertas, relacionamentos e demais registros).
            </p>
            <p className="text-sm text-rose-200 font-medium">Esta operação não poderá ser desfeita. Deseja continuar?</p>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-800/60 bg-amber-950/25 p-4 space-y-2" data-testid="force-delete-blocked">
            <div className="flex items-center gap-2 text-amber-200 font-semibold"><ShieldCheck className="h-5 w-5" /> Histórico protegido</div>
            <p className="text-sm text-amber-100">
              {name ? <><b>{name}</b> possui</> : <>Este {entityLabel} possui</>} histórico e não pode ser excluído. Use a opção <b>Inativar</b>.
            </p>
            <p className="text-xs text-amber-300/80">
              A exclusão com histórico só fica disponível quando o superadministrador liga o modo de teste desta empresa.
            </p>
          </div>
        )}
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="force-delete-cancel" className="w-full sm:w-auto">
            {blocked ? "Fechar" : "Cancelar"}
          </Button>
          {!blocked && (
            <Button data-testid="force-delete-confirm" onClick={onConfirm} disabled={busy} className="w-full sm:w-auto bg-rose-700 hover:bg-rose-600">
              {busy ? "Excluindo..." : "Excluir definitivamente"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
